import { ReactElement } from "react";
import PropTypes from "prop-types";
import {
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { scoreAverageAtom } from "./HomeAtoms";
import { Box } from "@chakra-ui/react";
import {
  CustomXAxisTick,
  getTicks,
  CustomDot,
  averageLabelPosition,
} from "../ResultCommon/ChartCustom";
import { ResultChartData } from "../../../types";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { NumericDataText } from "../NumericDataText";
import { useColorToken } from "../../../hooks/useColorToken";

type ReferenceDataType = {
  lineY: number;
  areaTop: number;
  areaBottom: number;
  areaColor: string;
};

/**
 * ホーム画面のグラフ
 * 解析結果グラフ画面 グラフ内でマーカーにマウスオーバー時のツールチップ表示
 * @constructor
 */
export const CustomTooltip = (
  props: TooltipContentProps,
): ReactElement | null => {
  const [[vitalityAve, mentalActivityAve, mi1Ave]] = useAtom(scoreAverageAtom);
  const payload = props.payload;
  const t = useModifiedTranslation();
  const backgroundColor = useColorToken("primary.bg_lv1");

  if (
    props.active &&
    payload !== null &&
    payload !== undefined &&
    payload.length > 0
  ) {
    const datingType = payload[0]?.payload?.datingType;
    const date = payload[0]?.payload?.date;
    const ValueResult = (): ReactElement => (
      <>
        {payload.map((data, key) => (
          <div key={key}>
            <NumericDataText fontWeight="300" display="inline-flex">
              {`${data.name} : `}{" "}
              {`${typeof data.value === "number" ? data.value.toFixed(1) : ""}`}
            </NumericDataText>
            {key < payload.length - 1 && <br />}
          </div>
        ))}
      </>
    );

    const average_text = t("Graph.common.average");

    const AverageResult = (): ReactElement => (
      <>
        {payload.map((data, key) => (
          <div key={key}>
            <NumericDataText fontWeight="300" display="inline-flex">
              {`${data.name}`}
              {average_text}
              {`: `}
              {data.name === t("Graph.common.vitality") &&
                (vitalityAve * 100).toFixed(1)}
              {data.name === t("Graph.common.mentalActivity") &&
                (mentalActivityAve * 100).toFixed(1)}
              {data.name === t("Graph.common.mi1Result") && mi1Ave.toFixed(1)}
            </NumericDataText>
            {key < payload.length - 1 && <br />}
          </div>
        ))}
      </>
    );

    // ツールチップ先頭行の日時
    const DateResult = (): ReactElement => (
      <Box display="inline-flex" alignItems="baseline">
        <NumericDataText letterSpacing={0.5} fontWeight={300}>
          {datingType === "year"
            ? `${dayjs(date).format("YYYY/M")}`
            : `${dayjs(date).format("YYYY/M/D")}`}
        </NumericDataText>
        {datingType !== "year" && (
          <NumericDataText
            fontSize="0.8rem"
            letterSpacing={0.5}
            fontWeight={300}
          >
            {`${dayjs(date).format("(ddd)")}`}
          </NumericDataText>
        )}
        <NumericDataText letterSpacing={0.5} fontWeight={300}>
          {/* の平均値 */}
          {datingType === "year"
            ? t("Graph.common.averageOf")
            : `${dayjs(date).format(" HH:mm:ss")}`}
        </NumericDataText>
      </Box>
    );

    return (
      <div
        style={{
          backgroundColor: backgroundColor,
          padding: 4,
          textAlign: "left",
        }}
      >
        <DateResult />
        <br />
        <ValueResult />
        <br />
        <AverageResult />
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      payload: PropTypes.object,
    }),
  ),
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export function HomeChartBody(
  props: Readonly<{
    data: ResultChartData[];
    reference: ReferenceDataType;
    tickArea: number[];
    name: string;
    dataKey: string;
    hasData: boolean;
    outputStenScore: boolean;
    aspectRatio: number;
  }>,
): ReactElement {
  const t = useModifiedTranslation();

  const labelValueName = props.outputStenScore
    ? t("Graph.common.average")
    : t("Graph.common.averageYour");
  const yAxisWidthSize = props.outputStenScore ? 16 : 0;
  const yAxisTickFontSize = props.outputStenScore ? 10 : 0;
  const noDataY1 = props.dataKey === "mi1_result" ? 10 : 100;
  const noDataY2 = props.dataKey === "mi1_result" ? 1 : 0;
  const referenceLineY =
    props.dataKey === "mi1_result"
      ? props.reference.lineY
      : props.reference.lineY * 100;
  const tickColor = useColorToken("common_text.gray");
  const referenceAreaColor = "#808080";
  const referenceLineColor = useColorToken("graph.referenceLine");
  return (
    <ResponsiveContainer width="100%" aspect={props.aspectRatio}>
      <ComposedChart
        data={props.data}
        margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
      >
        <YAxis
          domain={["dataMin", "dataMax"]}
          ticks={props.tickArea}
          axisLine={false}
          tickLine={false}
          width={yAxisWidthSize}
          tick={{
            fontSize: yAxisTickFontSize,
            fill: tickColor,
            fontFamily: "Oswald",
          }}
        />
        <XAxis
          tickLine={false}
          tick={<CustomXAxisTick />}
          ticks={getTicks(dayjs().startOf("day").unix() * 1000, 7)}
          dataKey="date"
          domain={["dataMin", "dataMax"]}
          interval={0}
          type="number"
        />
        <CartesianGrid vertical={false} />

        {!props.hasData && (
          <ReferenceArea
            y1={noDataY1}
            y2={noDataY2}
            fill="common.base"
            label={{
              value: "NO DATA",
              fontFamily: "Oswald",
              stroke: referenceAreaColor,
              fill: referenceAreaColor,
              fontSize: "26px",
              position: "center",
            }}
          />
        )}

        {!props.outputStenScore &&
          props.reference.areaTop !== 0 &&
          props.hasData && (
            <ReferenceArea
              y1={props.reference.areaTop}
              y2={props.reference.areaBottom}
              fill={`${props.reference.areaColor}33`}
              strokeOpacity={0}
            />
          )}

        <Tooltip content={CustomTooltip} wrapperStyle={{ zIndex: 1000 }} />
        <Line
          connectNulls
          name={props.name}
          strokeLinecap="round"
          isAnimationActive={false}
          dot={
            <CustomDot
              dotColor={props.reference.areaColor}
              strokeWidth="2"
              thresholdAlertValue={props.reference.areaBottom}
            />
          }
          dataKey={props.dataKey}
          strokeWidth={1}
          fillOpacity={1}
          stroke={props.reference.areaColor}
        />
        {!props.outputStenScore && props.reference.lineY !== 0 && (
          <ReferenceLine
            y={referenceLineY}
            strokeWidth={0.5}
            stroke={referenceLineColor}
          >
            <Label
              fontSize={7}
              fill={referenceLineColor}
              value={labelValueName}
              position={averageLabelPosition(
                props.data,
                referenceLineY,
                props.dataKey,
              )}
            />
          </ReferenceLine>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
